/**
 * Status / confirmation / priority vocabulary for the UI.
 *
 * Every ticket status and user-confirmation value the UI can ever render
 * comes from `@ithelpdesk/shared` (`TICKET_STATUSES`, `USER_CONFIRMATION_STATUSES`).
 * We never keep a second, parallel list of status strings here — the maps
 * below are typed as `Record<TicketStatus, ...>` / `Record<UserConfirmationStatus,
 * ...>`, so if the shared package ever adds or renames a value, this file
 * fails to typecheck until someone gives it a label. That is deliberate: a
 * status with no UI meaning is a bug, not a fallback case to paper over.
 */
import type { SlaState, TicketStatus, UserConfirmationStatus } from '@ithelpdesk/shared';
import { SLA_STATES, TICKET_STATUSES, USER_CONFIRMATION_STATUSES, wasUserConfirmed } from '@ithelpdesk/shared';

export { wasUserConfirmed };

/** Semantic tone -> the CSS custom properties a component reads. Never color alone: every tone is always paired with the `label` text. */
export type Tone = 'neutral' | 'info' | 'accent' | 'warning' | 'success' | 'danger';

/** A small shape, layered on top of color, so status is distinguishable without relying on hue perception either. */
export type Shape = 'circle' | 'diamond' | 'triangle' | 'square';

export interface StatusMeta {
  label: string;
  tone: Tone;
  shape: Shape;
  /** One line of extra context, shown in a Tooltip next to the label. */
  description?: string;
}

export const STATUS_META: Record<TicketStatus, StatusMeta> = {
  NEW: {
    label: 'New',
    tone: 'info',
    shape: 'circle',
    description: 'Submitted, not yet triaged.',
  },
  TRIAGED: {
    label: 'Triaged',
    tone: 'neutral',
    shape: 'circle',
    description: 'Categorized and prioritized, not yet assigned.',
  },
  ASSIGNED: {
    label: 'Assigned',
    tone: 'info',
    shape: 'circle',
    description: 'Assigned to a team or technician, work not yet started.',
  },
  IN_PROGRESS: {
    label: 'In Progress',
    tone: 'accent',
    shape: 'circle',
    description: 'A technician is actively working this ticket.',
  },
  PENDING_USER: {
    label: 'Waiting for User',
    tone: 'warning',
    shape: 'diamond',
    description: 'Paused until the requester responds.',
  },
  PENDING_EXTERNAL: {
    label: 'Waiting on Third Party',
    tone: 'warning',
    shape: 'diamond',
    description: 'Paused pending an external vendor or party.',
  },
  RESOLVED: {
    label: 'Resolved',
    tone: 'success',
    shape: 'circle',
    description: 'A fix was recorded. Awaiting the requester’s confirmation before closure.',
  },
  REOPENED: {
    label: 'Reopened',
    tone: 'danger',
    shape: 'triangle',
    description: 'The requester reported the issue was not actually resolved.',
  },
  CLOSED: {
    label: 'Closed',
    tone: 'neutral',
    shape: 'square',
    description: 'Resolution was confirmed and the ticket is complete.',
  },
  CANCELLED: {
    label: 'Cancelled',
    tone: 'neutral',
    shape: 'square',
    description: 'Withdrawn before completion.',
  },
};

/** Iterate in the shared package's canonical order — never a re-sorted or re-declared list. */
export const STATUS_ORDER: readonly TicketStatus[] = TICKET_STATUSES;

export function statusMeta(status: TicketStatus): StatusMeta {
  return STATUS_META[status];
}

// ---------------------------------------------------------------------------
// User confirmation status
//
// This is the language that must never let AUTO_CONFIRMED, ADMIN_OVERRIDE or NOT_REQUIRED
// read as "the requester confirmed it". Reporting and dashboards must use
// `wasUserConfirmed()` (re-exported above from @ithelpdesk/shared) wherever a
// boolean "did the user confirm" is needed — never `status === 'CONFIRMED'`
// spelled out again at the call site, and never `userConfirmationStatus !==
// 'PENDING'`, which would silently count AUTO_CONFIRMED/ADMIN_OVERRIDE as a
// real confirmation.
// ---------------------------------------------------------------------------

export interface ConfirmationMeta {
  label: string;
  tone: Tone;
  /** Shown in a Tooltip. Mandatory for the two values that are NOT a real user confirmation, so the UI never implies otherwise silently. */
  tooltip?: string;
}

export const CONFIRMATION_META: Record<UserConfirmationStatus, ConfirmationMeta> = {
  PENDING: {
    label: 'Awaiting requester',
    tone: 'warning',
  },
  CONFIRMED: {
    label: 'Confirmed by requester',
    tone: 'success',
  },
  REJECTED: {
    label: 'Requester reported unresolved',
    tone: 'danger',
  },
  AUTO_CONFIRMED: {
    label: 'Auto-closed — no reply',
    tone: 'neutral',
    tooltip: 'The requester never responded within the confirmation window.',
  },
  ADMIN_OVERRIDE: {
    label: 'Closed by admin override',
    tone: 'neutral',
    tooltip: 'Closed administratively. Not confirmed by the requester.',
  },
  NOT_REQUIRED: {
    label: 'Closed on resolution',
    tone: 'neutral',
    tooltip: 'The technician’s resolution closed this ticket. The requester was emailed and can reopen it.',
  },
};

export const CONFIRMATION_ORDER: readonly UserConfirmationStatus[] = USER_CONFIRMATION_STATUSES;

export function confirmationMeta(status: UserConfirmationStatus): ConfirmationMeta {
  return CONFIRMATION_META[status];
}

// ---------------------------------------------------------------------------
// SLA state
//
// `slaState` comes straight off the ticket DTO — this is a label/tone lookup
// only, never a place that computes or infers an SLA state of its own. Kept
// as a `Record<SlaState, ...>` for the same reason as `STATUS_META`: adding a
// new SLA state to the shared enum fails this file's typecheck until it gets
// a label, rather than silently rendering nothing.
// ---------------------------------------------------------------------------

export interface SlaMeta {
  label: string;
  tone: Tone;
}

export const SLA_META: Record<SlaState, SlaMeta> = {
  ON_TRACK: { label: 'On track', tone: 'neutral' },
  AT_RISK: { label: 'At risk', tone: 'warning' },
  BREACHED: { label: 'Breached', tone: 'danger' },
  PAUSED: { label: 'Paused', tone: 'neutral' },
  MET: { label: 'Met', tone: 'success' },
  NOT_APPLICABLE: { label: 'No SLA', tone: 'neutral' },
};

export const SLA_ORDER: readonly SlaState[] = SLA_STATES;

export function slaMeta(state: SlaState): SlaMeta {
  return SLA_META[state];
}

// ---------------------------------------------------------------------------
// Priority
//
// Unlike ticket status, priorities are admin-configurable rows
// (`packages/shared`'s `priorityRefSchema`: {id, code, name, rank}), not a
// fixed union — so there is no exhaustive shared enum to key off. We map the
// common codes an installation is likely to seed, and fall back to a
// rank-based tone so an unrecognized/custom priority still renders sensibly
// instead of looking broken.
// ---------------------------------------------------------------------------

export interface PriorityRefLike {
  code: string;
  name: string;
  rank: number;
}

/** The only tones a priority ever renders in — a strict subset of `Tone`, kept explicit so components can exhaustively style each one. */
export type PriorityTone = 'info' | 'neutral' | 'warning' | 'danger';

const PRIORITY_TONE_BY_CODE: Record<string, PriorityTone> = {
  LOW: 'info',
  MEDIUM: 'neutral',
  NORMAL: 'neutral',
  HIGH: 'warning',
  URGENT: 'danger',
  CRITICAL: 'danger',
};

/**
 * Lower rank = higher priority is the convention used by
 * `TICKET_LIST_SORT_FIELDS` ordering elsewhere in the app; ranks 1-4 map to
 * critical..low by default when the code itself is unrecognized.
 */
export function priorityTone(priority: PriorityRefLike): PriorityTone {
  const byCode = PRIORITY_TONE_BY_CODE[priority.code.toUpperCase()];
  if (byCode) return byCode;
  if (priority.rank <= 1) return 'danger';
  if (priority.rank === 2) return 'warning';
  if (priority.rank === 3) return 'neutral';
  return 'info';
}
